import { IconSettings } from "@tabler/icons-react";
import {
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { ProjectAgentsList } from "@/agents/components/project-agents-list";
import { useTRPC } from "@/infrastructure/trpc/react";
import { AppLayout, PageBreadcrumb } from "@/shared/layout";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import {
  OrganizationRoles,
  ProjectRoles,
} from "@vivus/common";

export const Route = createFileRoute("/app/project/$id/")({
  loader: async ({ context, params }) => {
    const projectData = await context.queryClient.query(
      context.trpc.project.getById.queryOptions({
        projectId: params.id,
      })
    );

    // Conditionally prefetch organization data
    if (projectData?.project?.organizationId) {
      void context.queryClient
        .query(
          context.trpc.organization.getById.queryOptions({
            id: projectData.project.organizationId,
          })
        )
        .catch(() => undefined);

      // Prefetch k8s clusters for add agent dialog
      void context.queryClient
        .query(
          context.trpc.k8sClusters.list.queryOptions({
            organizationId:
              projectData.project.organizationId,
          })
        )
        .catch(() => undefined);
    }

    // Prefetch project members
    void context.queryClient
      .query(
        context.trpc.projectPermissions.getProjectMembers.queryOptions(
          { projectId: params.id }
        )
      )
      .catch(() => undefined);

    // Prefetch agents data
    void context.queryClient
      .query(
        context.trpc.agents.listByProject.queryOptions({
          projectId: params.id,
        })
      )
      .catch(() => undefined);

    // Prefetch agent templates
    void context.queryClient
      .query(
        context.trpc.agents.listTemplates.queryOptions()
      )
      .catch(() => undefined);
  },
  component: ProjectPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function ProjectPage() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(
    trpc.project.getById.queryOptions({ projectId: id })
  );

  const { data: organizationData } = useSuspenseQuery(
    trpc.organization.getById.queryOptions({
      id: data?.project?.organizationId || "",
    })
  );

  const { data: members } = useSuspenseQuery(
    trpc.projectPermissions.getProjectMembers.queryOptions({
      projectId: id,
    })
  );

  // Force cache invalidation when project ID changes
  useEffect(() => {
    if (!id) return;
    void queryClient.invalidateQueries({
      queryKey: ["trpc", "apiKeys"],
    });
  }, [id, queryClient]);

  // Check if user can edit (org admin/owner or project admin/editor)
  const isOrgAdmin =
    data?.orgRole === OrganizationRoles.Admin ||
    data?.orgRole === OrganizationRoles.Owner;
  const canEdit =
    isOrgAdmin ||
    data?.projectRole === ProjectRoles.Admin ||
    data?.projectRole === ProjectRoles.Editor;

  const breadcrumbItems = [
    {
      label: organizationData?.organization?.name ?? "",
      to: "/app/organization/$id",
      params: { id: data?.project?.organizationId ?? "" },
    },
    {
      label: data?.project?.name ?? "",
      to: "/app/project/$id",
      params: { id },
    },
  ];

  return (
    <AppLayout
      title={<PageBreadcrumb items={breadcrumbItems} />}
      headerActions={
        <Link
          to="/app/project/$id/settings"
          params={{ id }}
        >
          <Button variant="secondary" size="sm">
            <IconSettings className="mr-2 size-4" />
            <span className="hidden sm:inline">
              Settings
            </span>
            <span className="sm:hidden">Settings</span>
          </Button>
        </Link>
      }
    >
      {/* AI Agents */}
      <div className="mb-6">
        <ProjectAgentsList
          projectId={id}
          organizationId={
            data?.project?.organizationId ?? ""
          }
          canEdit={canEdit}
        />
      </div>

      {/* Team Members */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold">
          Team Members
          {(members?.length ?? 0) > 3 &&
            ` (${members?.length})`}
        </h3>
        <Card className="overflow-hidden">
          <CardContent variant="p0">
            <div className="divide-y">
              {members?.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No team members yet
                </div>
              ) : (
                members?.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      {member.image ? (
                        <img
                          src={member.image}
                          alt={member.name ?? "User"}
                          className="size-10 rounded-none"
                        />
                      ) : (
                        <div className="flex size-10 items-center justify-center rounded-none bg-primary/10">
                          <span className="text-sm font-medium">
                            {member.name
                              ?.charAt(0)
                              .toUpperCase() ?? "?"}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="font-medium">
                          {member.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {member.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-muted px-3 py-1 text-sm capitalize">
                        {member.role}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
